import { EyeOff, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { TRASH_RETENTION_DAYS } from './trashRetention';

export type DeletePreviewPhoto = {
  // React key + nothing else — the dialog never addresses the backend.
  key: string;
  caption?: string | null;
  thumbnailUrl?: string;
  hidden?: boolean;
};

export const DELETE_PREVIEW_LIMIT = 8;

type DeleteConfirmDialogProps = {
  mode: 'trash' | 'purge';
  photos: DeletePreviewPhoto[];
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * Destructive confirmation for both delete steps (DV-2524). Structural photo
 * type so trash mode can be fed from the grid's working set and purge mode from
 * the trash listing without either shape leaking in.
 */
const DeleteConfirmDialog = ({
  mode,
  photos,
  onCancel,
  onConfirm
}: DeleteConfirmDialogProps) => {
  const count = photos.length;
  const isPurge = mode === 'purge';
  const plural = count === 1 ? 'Photo' : `${count} Photos`;
  const title = isPurge
    ? `Delete ${plural} Permanently?`
    : `Move ${plural} to Trash?`;
  const subject = count === 1 ? 'This photo' : 'These photos';
  // A tile is only meaningful with an image behind it. `thumbnailUrl` is
  // optional — a purge target whose derivative caches the backend pruned on
  // trash arrives without one — and `<img src={undefined}>` paints the
  // broken-image glyph rather than nothing. Drop those from the strip; they
  // still land in the "+N" count, and a set with no thumbnails at all just
  // renders the copy without a strip.
  const preview = photos
    .filter((photo) => !!photo.thumbnailUrl)
    .slice(0, DELETE_PREVIEW_LIMIT);
  const overflow = count - preview.length;

  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
      size="md"
      title={title}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onCancel} autoFocus>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm}>
            <Trash2 className="h-4 w-4" />
            {isPurge ? 'Delete Permanently' : 'Move to Trash'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {preview.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {preview.map((photo) => (
              <span
                key={photo.key}
                className="relative h-16 w-24 overflow-hidden rounded-md ring-1 ring-border"
              >
                <img
                  src={photo.thumbnailUrl}
                  alt=""
                  role="presentation"
                  className="h-full w-full object-cover"
                />
                {photo.hidden && (
                  // role="img" is load-bearing: a plain span's implicit role is
                  // `generic`, which ARIA prohibits from having a computed
                  // accessible name, so aria-label alone would be announced by
                  // nothing. (RTL's getByLabelText matches the attribute
                  // regardless, so a test cannot catch this.) Same idiom as the
                  // ui/*-loader primitives. The over-image bg-black/text-white
                  // chrome is deliberately theme-invariant, matching
                  // AssetsImageCard's hidden flag.
                  <span
                    role="img"
                    aria-label={`${photo.caption ?? 'Photo'} (hidden)`}
                    className="absolute inset-0 flex items-center justify-center bg-black/60 text-white"
                  >
                    <EyeOff className="h-4 w-4" aria-hidden />
                  </span>
                )}
              </span>
            ))}
            {overflow > 0 && (
              <span className="flex h-16 w-24 items-center justify-center rounded-md bg-muted text-sm font-medium text-muted-foreground">
                +{overflow}
              </span>
            )}
          </div>
        )}
        {isPurge ? (
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">
              This can&apos;t be undone.
            </span>{' '}
            {subject} will be permanently deleted and can&apos;t be recovered.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            {subject} will be available in the Trash for {TRASH_RETENTION_DAYS}{' '}
            days.
          </p>
        )}
      </div>
    </Modal>
  );
};

export default DeleteConfirmDialog;
